'use client';
import { useState } from 'react';
import styles from './global-email-signup.module.scss';
import { useTranslations } from 'next-intl';
import MarkdownParser from 'components/rich-text/markdown-parser';
import Button from 'components/controls/buttons/button';
import CheckMark from 'assets/icons/language-filter/checkmark.svg';
import pushToDataLayer from 'lib/analytics/pushToDataLayer';
import { emailSignup } from 'lib/email/emailSignup';

const GlobalEmailSignupForm = ({
	body,
	title,
	analyticsEventName,
	emailCode,
}: IProps) => {
	const [email, setEmail] = useState('');
	const [showSuccess, setShowSuccess] = useState(false);
	const [showError, setShowError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [isValid, setIsValid] = useState(true);
	const [isEmailDirty, setIsEmailDirty] = useState(false);
	const t = useTranslations('emailSignup');

	const checkValidity = (event) => {
		const emailInput = event.target;
		setIsValid(emailInput.validity.valid);
		setIsEmailDirty(true);
	};

	const submit = async (e) => {
		e.preventDefault();
		//Hide Previous Error State
		if (isValid) {
			setShowError(false);
			setShowSuccess(false);
			const res = await emailSignup({ email, emailCode });
			if (res.success) {
				setShowSuccess(true);
				if (analyticsEventName) {
					pushToDataLayer({
						Category: 'Email',
						Action: 'Sign up',
						event: analyticsEventName,
					});
				}
			} else {
				setShowError(true);
				setErrorMessage(res.message);
			}
		}
	};

	return (
		<form className={styles.form}>
			{!!title && (
				<h2>
					<MarkdownParser>{title}</MarkdownParser>
				</h2>
			)}
			{!!body && (
				<div className={styles.body}>
					<MarkdownParser>{body}</MarkdownParser>
				</div>
			)}
			<div className={styles.inputContainer}>
				{!showSuccess && (
					<>
						<span className={styles.emailLabel}>{t('emailAddress')}</span>
						<input
							className={`${styles.emailInput} ${!isEmailDirty ? styles.noError : ''}`}
							id="global-email-signup"
							type="email"
							autoComplete="email"
							placeholder="example@website.com"
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
							}}
							onBlur={checkValidity}
							required={true}
						/>
						{!isValid && isEmailDirty && (
							<span className={styles.errorMessage}>{t('invalidEmail')}</span>
						)}
						{showError && (
							<span className={styles.errorMessage}>{errorMessage}</span>
						)}
						<Button
							className={styles.button}
							appearance="primary"
							type="submit"
							onClick={submit}
						>
							{t('signUp')}
						</Button>
					</>
				)}
				{showSuccess && (
					<div className={styles.successMessage}>
						<CheckMark height="25" width="25" /> <span>{t('thankYou')}</span>
					</div>
				)}
			</div>
		</form>
	);
};

export interface IProps {
	analyticsEventName?: string;
	body: string;
	emailCode: string;
	title: string;
}

export default GlobalEmailSignupForm;
